import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getGame } from "@/game/games";
import API from "@/lib/api";
import AsteroidsEngine from "@/game/AsteroidsEngine";
import InvadersEngine from "@/game/InvadersEngine";
import { toast } from "sonner";
import { Lock, ArrowLeft, Crosshair, Heart, Clock, Trophy } from "lucide-react";
import sounds from "@/lib/sounds";

const SHIP_META = [
  { name: "Viper", color: "#00F0FF", desc: "Quick and nimble starter fighter", svg: "M12 2 L22 22 L12 17 L2 22 Z" },
  { name: "Interceptor", color: "#FF003C", desc: "Advanced hull with rapid fire", svg: "M12 0 L18 10 L24 22 L12 17 L0 22 L6 10 Z" },
  { name: "Destroyer", color: "#8A2BE2", desc: "Heavy armor, devastating weapons", svg: "M12 0 L19 8 L26 22 L12 16 L-2 22 L5 8 Z" },
  { name: "Phantom", color: "#FFD700", desc: "Elite stealth craft with ultimate power", svg: "M12 0 L17 5 L24 10 L26 20 L18 24 L12 22 L6 24 L-2 20 L0 10 L7 5 Z" },
];


export default function ArcadeGame() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const [phase, setPhase] = useState("loading"); // loading, select, countdown, playing, gameover
  const [gameConfig, setGameConfig] = useState(null);
  const [ships, setShips] = useState([]);
  const [selectedShip, setSelectedShip] = useState(0);
  const { gameId } = useParams();
  const game = gameId;
  const gameMeta = getGame(gameId);
  const startMsRef = useRef(0);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [gameTime, setGameTime] = useState(0);
  const [countVal, setCountVal] = useState(3);
  const [loadout, setLoadout] = useState({});
  const timerRef = useRef(null);

  // Load game config + ships + loadout
  useEffect(() => {
    if (!gameMeta) { navigate("/arcade"); return; }
    const load = async () => {
      try {
        const [configRes, shipsRes, loadoutRes] = await Promise.all([
          API.post("/arcade/start", null, { params: { game } }),
          API.get("/student/ships"),
          API.get("/shop/loadout"),
        ]);
        setGameConfig(configRes.data);
        setShips(shipsRes.data.ships);
        setSelectedShip(Math.min(configRes.data.ship_level, 3));
        setLoadout(loadoutRes.data.loadout || {});
        setPhase("select");
      } catch (err) {
        toast.error("No reward earned yet!", { id: "no-reward" });
        navigate("/student");
      }
    };
    load();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []); // eslint-disable-line

  const startCountdown = () => {
    setPhase("countdown");
    setCountVal(3);
    let c = 3;
    const id = setInterval(() => {
      c--;
      setCountVal(c);
      if (c <= 0) {
        clearInterval(id);
        setPhase("playing");
      }
    }, 800);
  };

  // Start game engine
  useEffect(() => {
    if (phase !== "playing" || !canvasRef.current || !gameConfig) return;
    const cvs = canvasRef.current;
    cvs.width = Math.min(960, window.innerWidth - 32);
    cvs.height = Math.min(640, window.innerHeight - 210);

    const prevScore = { current: 0 };
    const prevLives = { current: 3 };
    const Engine = game === "invaders" ? InvadersEngine : AsteroidsEngine;
    const engine = new Engine(cvs, {
      shipType: selectedShip,
      loadout: loadout,
      onScore: (s) => {
        if (s > prevScore.current) sounds.explosion();
        prevScore.current = s;
        setScore(s);
      },
      onLives: (l) => {
        if (l < prevLives.current) sounds.hit();
        prevLives.current = l;
        setLives(l);
      },
      onGameOver: (s) => {
        sounds.gameOver();
        setScore(s);
        handleGameOver(s);
      },
    });
    engineRef.current = engine;
    engine.start();

    // Shoot sound on Space key
    const shootHandler = (e) => { if (e.code === "Space" && game === "asteroids") sounds.shoot(); };
    window.addEventListener("keydown", shootHandler);
    return () => { engine.stop(); window.removeEventListener("keydown", shootHandler); };
  }, [phase, selectedShip, gameConfig, game]); // eslint-disable-line

  // Game timer
  useEffect(() => {
    if (phase !== "playing" || !gameConfig) return;
    const startMs = Date.now();
    startMsRef.current = startMs;
    const limitMs = gameConfig.time_limit * 1000;
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startMs;
      if (gameConfig.mode === "standard") {
        const rem = Math.max(0, (limitMs - elapsed) / 1000);
        setGameTime(rem);
        if (rem <= 0) {
          clearInterval(timerRef.current);
          if (engineRef.current && !engineRef.current.over) engineRef.current.forceEnd();
        }
      } else {
        setGameTime(elapsed / 1000);
        if (elapsed >= limitMs) {
          clearInterval(timerRef.current);
          if (engineRef.current && !engineRef.current.over) engineRef.current.forceEnd();
        }
      }
    }, 100);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [phase, gameConfig]);

  const handleGameOver = useCallback(async (finalScore) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setPhase("gameover");
    try {
      const duration = startMsRef.current ? (Date.now() - startMsRef.current) / 1000 : 0;
      await API.post("/arcade/end", { score: finalScore, duration, game, session_id: gameConfig?.session_id });
    } catch (err) {
      console.error("Failed to save game", err);
    }
  }, [game, gameConfig]);

  if (phase === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-mono text-cyber animate-pulse tracking-widest">INITIALIZING ARCADE...</div>
      </div>
    );
  }

  if (phase === "select") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="ship-select-screen">
        <div className="w-full max-w-3xl">
          <button onClick={() => navigate("/arcade")} className="flex items-center gap-2 text-white/40 hover:text-white mb-8 font-body text-sm transition-colors duration-200" data-testid="back-to-lobby">
            <ArrowLeft className="w-4 h-4" /> Back to Lobby
          </button>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-2 tracking-tighter">
            <span className="neon-text-cyber" data-testid="selected-game-name">{gameMeta?.name}</span>: Choose Your Ship
          </h1>
          <p className="font-body text-white/40 mb-2">
            Mode: <span className={gameConfig?.mode === "bonus" ? "text-cyber font-bold" : "text-white/60"}>
              {gameConfig?.mode === "bonus" ? "BONUS (Until Death)" : "Standard (60s)"}
            </span>
          </p>
          <p className="font-body text-white/30 text-sm mb-6">{gameMeta?.controls}</p>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {SHIP_META.map((ship, i) => {
              const unlocked = ships[i]?.unlocked;
              const selected = selectedShip === i;
              return (
                <button
                  key={i}
                  onClick={() => unlocked && setSelectedShip(i)}
                  disabled={!unlocked}
                  data-testid={`select-ship-${i}`}
                  className={`relative p-5 rounded-xl border-2 transition-all duration-200 text-center ${
                    selected && unlocked
                      ? "ship-card-selected"
                      : unlocked
                      ? "border-white/10 hover:border-white/20 hover:-translate-y-1"
                      : "border-white/5 opacity-30 cursor-not-allowed"
                  }`}
                  style={selected && unlocked ? { borderColor: ship.color } : {}}
                >
                  {!unlocked && <Lock className="w-4 h-4 text-white/20 absolute top-3 right-3" />}
                  <div className="flex justify-center mb-4">
                    <svg width="56" height="56" viewBox="0 0 26 26">
                      <path d={ship.svg} fill={unlocked ? ship.color : "#333"} style={unlocked ? { filter: `drop-shadow(0 0 8px ${ship.color})` } : {}} />
                    </svg>
                  </div>
                  <p className="font-heading text-sm font-bold text-white">{ship.name}</p>
                  <p className="font-body text-xs text-white/30 mt-1">{ship.desc}</p>
                </button>
              );
            })}
          </div>

          <button
            onClick={startCountdown}
            data-testid="launch-game-btn"
            className="w-full btn-cyber py-4 rounded-full font-heading text-lg tracking-wider"
          >
            LAUNCH
          </button>
        </div>
      </div>
    );
  }

  if (phase === "countdown") {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="countdown-screen">
        <div className="text-center">
          <p className="font-mono text-8xl font-bold neon-text-cyber animate-streak-pop">{countVal}</p>
          <p className="font-body text-white/40 mt-4">Get ready...</p>
        </div>
      </div>
    );
  }

  if (phase === "gameover") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="gameover-screen">
        <div className="text-center animate-slide-up">
          <Trophy className="w-16 h-16 text-cyber mx-auto mb-6" />
          <h1 className="font-heading text-5xl font-black text-white mb-4 tracking-tighter">GAME OVER</h1>
          <p className="font-mono text-4xl text-cyber font-bold mb-2" data-testid="final-score">{score.toLocaleString()}</p>
          <p className="font-body text-white/40 mb-8">points scored</p>
          <button
            onClick={() => navigate("/student")}
            data-testid="return-dashboard-btn"
            className="btn-cyber px-8 py-3 rounded-full font-heading tracking-wider"
          >
            RETURN TO BASE
          </button>
        </div>
      </div>
    );
  }

  // Playing
  return (
    <div className="min-h-screen flex flex-col items-center pt-20 bg-void relative" data-testid="arcade-playing">
      {/* HUD */}
      <div className="fixed top-16 left-0 right-0 z-40 px-4">
        <div className="max-w-4xl mx-auto glass-surface rounded-xl px-5 py-2 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2" data-testid="hud-score">
              <Crosshair className="w-4 h-4 text-cyber" />
              <span className="font-mono text-xl font-bold text-white">{score.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-1" data-testid="hud-lives">
              {Array.from({ length: 3 }).map((_, i) => (
                <Heart key={i} className={`w-4 h-4 ${i < lives ? "text-magenta fill-magenta" : "text-white/10"}`} />
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2" data-testid="hud-timer">
            <Clock className="w-4 h-4 text-white/40" />
            <span className={`font-mono text-lg font-bold ${
              gameConfig?.mode === "standard" && gameTime <= 10 ? "text-magenta" : "text-white/60"
            }`}>
              {gameConfig?.mode === "standard"
                ? `${Math.ceil(gameTime)}s`
                : `${Math.floor(gameTime / 60)}:${String(Math.floor(gameTime % 60)).padStart(2, "0")}`
              }
            </span>
            <span className={`font-mono text-xs px-2 py-0.5 rounded-full ${
              gameConfig?.mode === "bonus" ? "bg-cyber/10 text-cyber" : "bg-white/5 text-white/30"
            }`}>
              {gameConfig?.mode?.toUpperCase()}
            </span>
          </div>
        </div>
      </div>

      <canvas
        ref={canvasRef}
        className="rounded-xl border border-white/5"
        data-testid="game-canvas"
      />
    </div>
  );
}
